import React, { useState, useEffect } from 'react';
import { 
  getUsersStravaConnection, 
  syncUserActivities, 
  syncAllUsersActivities,
  UserStravaConnection, 
  StravaConnectionStatus,
  SyncAllUsersResponse,
  getNextSyncSchedules,
  NextSyncSchedulesResponse
} from '../../services/systemService';
import { formatDateFromArray } from '../../utils/formatters';
import { getApiUrl } from '../../utils/config';
import DatePickerCustom from '../../components/DatePickerCustom';
import { 
  FaSync, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaExclamationTriangle, 
  FaSpinner, 
  FaUser, 
  FaCalendarAlt,
  FaTimes,
  FaInfoCircle,
  FaFilter,
  FaClock,
  FaSearch
} from 'react-icons/fa';

import './sync.css';

const Sync: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<StravaConnectionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [syncingAll, setSyncingAll] = useState<boolean>(false);
  const [syncingUsers, setSyncingUsers] = useState<{[key: number]: boolean}>({});
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Thêm state để lọc người dùng chưa kết nối
  const [showOnlyDisconnected, setShowOnlyDisconnected] = useState<boolean>(false);
  // State mới để lưu từ khóa tìm kiếm theo tên
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Đổi giá trị mặc định thành ngày hiện tại
  const getCurrentDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  };
  
  const [syncDate, setSyncDate] = useState<string>(getCurrentDate());
  const [apiCheckStatus, setApiCheckStatus] = useState<string>('');
  const [syncResult, setSyncResult] = useState<SyncAllUsersResponse | null>(null);
  const [showSyncResultDialog, setShowSyncResultDialog] = useState<boolean>(false);
  const [syncSchedules, setSyncSchedules] = useState<NextSyncSchedulesResponse | null>(null);
  const [loadingSyncSchedules, setLoadingSyncSchedules] = useState<boolean>(false);

  // Thêm kiểm tra kết nối API khi component mount
  useEffect(() => {
    const checkApiConnection = async () => {
      try {
        const token = localStorage.getItem('jwt_token');
        const authHeader = localStorage.getItem('auth_header');
        
        // Tìm thêm token từ các nguồn khác
        const possibleTokenSources = [
          { name: 'jwt_token', value: localStorage.getItem('jwt_token') },
          { name: 'auth_token', value: localStorage.getItem('auth_token') },
          { name: 'token', value: localStorage.getItem('token') },
          { name: 'access_token', value: localStorage.getItem('access_token') }
        ];
        
        const tokenInfo = possibleTokenSources
          .filter(src => src.value)
          .map(src => `${src.name}: ${src.value ? src.value.substring(0, 10) + '...' : 'Không có'}`)
          .join('\n');
        
        setApiCheckStatus(`Thông tin token: \n${tokenInfo}`);
        
        // Thử gọi API bằng fetch trực tiếp để debug
        const response = await fetch(getApiUrl('/api/stats/users/strava-connection'), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        setApiCheckStatus(prev => prev + `\n\nStatus API: ${response.status}`);
        
        if (!response.ok) {
          const errorText = await response.text();
          setApiCheckStatus(prev => prev + `\nError: ${errorText}`);
        } else {
          const data = await response.json();
          setApiCheckStatus(prev => prev + `\nAPI Response OK: ${data.result || 'Không xác định'}`);
        }
      } catch (err: any) {
        setApiCheckStatus(prev => prev + `\nLỗi kết nối: ${err.message}`);
      }
    };
    
    checkApiConnection();
  }, []);

  // Lấy danh sách người dùng và trạng thái kết nối
  useEffect(() => {
    const fetchConnectionStatus = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Thêm debug info
        console.log('Bắt đầu gọi API getUsersStravaConnection');
        
        const data = await getUsersStravaConnection();
        console.log('Dữ liệu nhận được:', data);
        setConnectionStatus(data);
      } catch (err: any) {
        console.error('Lỗi chi tiết khi lấy thông tin kết nối:', err);
        setError('Lỗi khi lấy thông tin kết nối Strava: ' + (err.message || 'Không rõ lỗi'));
      } finally {
        setLoading(false);
      }
    };

    fetchConnectionStatus();
  }, []);

  // Lấy thông tin lịch trình đồng bộ
  useEffect(() => {
    const fetchNextSyncSchedules = async () => {
      try {
        setLoadingSyncSchedules(true);
        setError(null);
        
        const data = await getNextSyncSchedules();
        setSyncSchedules(data);
      } catch (err: any) {
        console.error('Lỗi chi tiết khi lấy thông tin lịch trình đồng bộ:', err);
        // Không hiển thị lỗi cho người dùng để không làm gián đoạn trải nghiệm
      } finally {
        setLoadingSyncSchedules(false);
      }
    };

    fetchNextSyncSchedules();
  }, []);

  // Hàm đồng bộ hoạt động của một người dùng
  const handleSyncUser = async (userId: number) => {
    try {
      // Đánh dấu đang đồng bộ cho người dùng này
      setSyncingUsers(prev => ({ ...prev, [userId]: true }));
      setError(null);
      setSuccessMessage(null);

      const syncResult = await syncUserActivities(userId, syncDate);
      
      // Hiển thị thông báo thành công
      setSuccessMessage(`Đã đồng bộ thành công ${syncResult} hoạt động cho người dùng`);

      // Cập nhật lại danh sách sau khi đồng bộ
      const updatedStatus = await getUsersStravaConnection();
      setConnectionStatus(updatedStatus);
    } catch (err: any) {
      console.error(`Lỗi khi đồng bộ người dùng ${userId}:`, err);
      setError(err.message || `Không thể đồng bộ hoạt động cho người dùng`);
    } finally {
      // Đánh dấu kết thúc đồng bộ cho người dùng này
      setSyncingUsers(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Hàm đồng bộ hoạt động của tất cả người dùng
  const handleSyncAll = async () => {
    try {
      setSyncingAll(true);
      setError(null);
      setSuccessMessage(null);

      const result = await syncAllUsersActivities();
      
      // Lưu kết quả đồng bộ và hiển thị dialog
      setSyncResult(result);
      setShowSyncResultDialog(true);
      
      // Hiển thị thông báo thành công
      setSuccessMessage(`Đã đồng bộ thành công ${result.totalActivitiesSynced} hoạt động cho ${result.successfulUsers}/${result.totalUsers} người dùng`);

      // Cập nhật lại danh sách sau khi đồng bộ
      const updatedStatus = await getUsersStravaConnection();
      setConnectionStatus(updatedStatus);
    } catch (err: any) {
      console.error('Lỗi khi đồng bộ tất cả người dùng:', err);
      setError(err.message || 'Không thể đồng bộ hoạt động cho tất cả người dùng');
    } finally {
      setSyncingAll(false);
    }
  };

  // Xử lý thay đổi ngày đồng bộ
  const handleDateChange = (date: Date | null) => {
    const dateStr = date ? date.toISOString().split('T')[0] : '';
    setSyncDate(dateStr);
  };

  // Xử lý thay đổi checkbox lọc người dùng chưa kết nối
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyDisconnected(e.target.checked);
  };

  // Cập nhật hàm xử lý search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Render trạng thái kết nối
  const renderConnectionStatus = (connected: boolean, expireDate: number[] | null) => {
    if (connected) {
      return (
        <span className="connection-status connected">
          <FaCheckCircle /> Đã kết nối
          {expireDate && (
            <span className="expire-date">
              (Hết hạn: {formatDateFromArray(expireDate)})
            </span>
          )}
        </span>
      );
    } else {
      return (
        <span className="connection-status disconnected">
          <FaTimesCircle /> Chưa kết nối
        </span>
      );
    }
  };

  // Component dialog kết quả đồng bộ
  const SyncResultDialog = () => {
    if (!syncResult || !showSyncResultDialog) return null;

    return (
      <div className="sync-dialog-overlay">
        <div className="sync-dialog">
          <div className="sync-dialog-header">
            <h2>Kết quả đồng bộ dữ liệu</h2>
            <button className="close-button" onClick={() => setShowSyncResultDialog(false)}>
              <FaTimes />
            </button>
          </div>
          <div className="sync-dialog-content">
            <div className="sync-summary">
              <div className="summary-row">
                <div className="summary-item">
                  <span className="summary-label">Tổng hoạt động đã đồng bộ:</span>
                  <span className="summary-value">{syncResult.totalActivitiesSynced}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Tổng người dùng:</span>
                  <span className="summary-value">{syncResult.totalUsers}</span>
                </div>
              </div>
              <div className="summary-row">
                <div className="summary-item">
                  <span className="summary-label">Người dùng thành công:</span>
                  <span className="summary-value success">{syncResult.successfulUsers}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Người dùng thất bại:</span>
                  <span className="summary-value error">{syncResult.failedUsers}</span>
                </div>
              </div>
              <div className="summary-row">
                <div className="summary-item">
                  <span className="summary-label">Thời gian bắt đầu:</span>
                  <span className="summary-value">{syncResult.startTime}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Thời gian kết thúc:</span>
                  <span className="summary-value">{syncResult.endTime}</span>
                </div>
              </div>
              <div className="summary-row">
                <div className="summary-item full-width">
                  <span className="summary-label">Thời gian xử lý:</span>
                  <span className="summary-value">{syncResult.durationSeconds} giây</span>
                </div>
              </div>
            </div>
            
            <h3>Chi tiết người dùng</h3>
            <div className="user-details-list">
              {syncResult.userDetails.map((user) => (
                <div key={user.userId} className={`user-detail-item ${user.status}`}>
                  <div className="user-info">
                    <strong>{user.userName}</strong>
                    <span className="user-email">{user.email}</span>
                  </div>
                  <div className="sync-status">
                    <span className={`status-badge ${user.status}`}>
                      {user.status === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
                      {user.status === 'success' ? 'Thành công' : 'Thất bại'}
                    </span>
                    <span className="sync-message">
                      <FaInfoCircle /> {user.message}
                    </span>
                    <span className="activity-count">
                      Hoạt động đã đồng bộ: {user.activitiesSynced}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            {syncResult.errors.length > 0 && (
              <>
                <h3>Lỗi</h3>
                <div className="error-list">
                  {syncResult.errors.map((error, index) => (
                    <div key={index} className="error-item">
                      <FaExclamationTriangle /> {error}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="sync-dialog-footer">
            <button onClick={() => setShowSyncResultDialog(false)}>Đóng</button>
          </div>
        </div>
      </div>
    );
  };

  // Format thời gian cho dễ đọc
  const formatDateTime = (dateTime: string | number[]): string => {
    try {
      let date: Date;
      
      if (Array.isArray(dateTime)) {
        // Xử lý mảng [năm, tháng, ngày, giờ, phút, giây, ...]
        // Chú ý: trong Java tháng bắt đầu từ 1, nhưng JavaScript từ 0
        date = new Date(
          dateTime[0], // year
          dateTime[1] - 1, // month (giảm 1 vì JS tháng bắt đầu từ 0)
          dateTime[2], // day
          dateTime[3] || 0, // hour
          dateTime[4] || 0, // minute
          dateTime[5] || 0  // second
        );
      } else {
        date = new Date(dateTime);
      }
      
      return date.toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      console.error('Lỗi khi format thời gian:', err);
      return Array.isArray(dateTime) ? 'Không xác định' : dateTime;
    }
  };
  
  // Component hiển thị thông tin lịch trình đồng bộ
  const SyncSchedulesInfo = () => {
    if (!syncSchedules) return null;
    
    return (
      <div className="sync-schedules-section">
        <h2 className="sync-schedules-title">
          <FaClock /> Lịch trình đồng bộ tự động
        </h2>
        
        <div className="server-time">
          <strong>Thời gian máy chủ:</strong> {formatDateTime(syncSchedules.serverTime)}
        </div>
        
        <div className="sync-schedules-grid">
          <div className="sync-schedule-item">
            <h3>{syncSchedules.refreshTokens.description}</h3>
            <p className="next-execution">
              <FaCalendarAlt /> Lần tiếp theo: {formatDateTime(syncSchedules.refreshTokens.nextExecution)}
            </p>
            <p className="schedule">{syncSchedules.refreshTokens.schedule}</p>
          </div>
          
          <div className="sync-schedule-item">
            <h3>{syncSchedules.syncActivities.description}</h3>
            <p className="next-execution">
              <FaCalendarAlt /> Lần tiếp theo: {formatDateTime(syncSchedules.syncActivities.nextExecution)}
            </p>
            <p className="schedule">{syncSchedules.syncActivities.schedule}</p>
          </div>
          
          <div className="sync-schedule-item">
            <h3>{syncSchedules.fullSyncActivities.description}</h3>
            <p className="next-execution">
              <FaCalendarAlt /> Lần tiếp theo: {formatDateTime(syncSchedules.fullSyncActivities.nextExecution)}
            </p>
            <p className="schedule">{syncSchedules.fullSyncActivities.schedule}</p>
          </div>
          
          <div className="sync-schedule-item">
            <h3>{syncSchedules.checkExpiredTokens.description}</h3>
            <p className="next-execution">
              <FaCalendarAlt /> Lần tiếp theo: {formatDateTime(syncSchedules.checkExpiredTokens.nextExecution)}
            </p>
            <p className="schedule">{syncSchedules.checkExpiredTokens.schedule}</p>
          </div>
        </div>
      </div>
    );
  };

  // Cập nhật logic lọc danh sách người dùng để kết hợp cả tìm kiếm và lọc
  const filteredUsers = connectionStatus?.users.filter(user => {
    // Đầu tiên kiểm tra điều kiện filter theo trạng thái kết nối (nếu đang bật)
    const matchesConnectionFilter = showOnlyDisconnected ? !user.stravaConnected : true;
    
    // Sau đó kiểm tra điều kiện tìm kiếm theo tên
    const matchesSearch = searchQuery
      ? user.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    
    // Trả về true chỉ khi thỏa mãn cả hai điều kiện
    return matchesConnectionFilter && matchesSearch;
  }) || [];

  return (
    <div className="sync-container">
      <div className="sync-header">
        <h1>Đồng bộ dữ liệu Strava</h1>
        <p>Quản lý đồng bộ hoạt động của người dùng từ Strava</p>
      </div>

      {error && (
        <div className="sync-alert error">
          <FaExclamationTriangle /> {error}
        </div>
      )}

      {successMessage && (
        <div className="sync-alert success">
          <FaCheckCircle /> {successMessage}
        </div>
      )}

      {/* Thêm phần hiển thị lịch trình đồng bộ */}
      {loadingSyncSchedules ? (
        <div className="sync-schedules-loading">
          <FaSpinner className="spinning" /> Đang tải thông tin lịch trình đồng bộ...
        </div>
      ) : (
        <SyncSchedulesInfo />
      )}

      <div className="sync-controls">
        <div className="date-control">
          <label htmlFor="sync-date">
            <FaCalendarAlt style={{ marginRight: '8px' }} />
            Đồng bộ từ ngày:
          </label>
          <div className="date-picker-container">
            <DatePickerCustom
              label=""
              value={syncDate}
              onChange={handleDateChange}
              className="sync-date-picker"
            />
          </div>
        </div>
        <button 
          className="sync-all-button" 
          onClick={handleSyncAll}
          disabled={syncingAll || loading}
        >
          {syncingAll ? <FaSpinner className="spinning" /> : <FaSync />} 
          Đồng bộ tất cả người dùng
        </button>
      </div>

      <div className="connection-summary">
        {connectionStatus && (
          <>
            <div className="summary-item">
              <span className="summary-label">Tổng số người dùng:</span>
              <span className="summary-value">{connectionStatus.totalUserCount}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Đã kết nối Strava:</span>
              <span className="summary-value">{connectionStatus.connectedUserCount}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Chưa kết nối:</span>
              <span className="summary-value">{connectionStatus.disconnectedUserCount}</span>
            </div>
          </>
        )}
      </div>

      <div className="filter-controls">
        <label className="filter-checkbox">
          <input 
            type="checkbox" 
            checked={showOnlyDisconnected}
            onChange={handleFilterChange}
          />
          <FaFilter style={{ marginRight: '8px' }} />
          Chỉ hiển thị người dùng chưa kết nối Strava
        </label>
        
        {/* Thêm input tìm kiếm */}
        <div className="search-box">
          {/* Button tim kiem có màu đỏ */}
          <FaSearch className="search-icon" style={{ color: 'red' }} />
          <input
            type="text"
            placeholder="Tìm kiếm theo tên người dùng..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchQuery && (
            <button 
              className="clear-search" 
              onClick={() => setSearchQuery('')}
              title="Xóa tìm kiếm"
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <FaSpinner className="spinning" />
          <p>Đang tải danh sách người dùng...</p>
        </div>
      ) : connectionStatus && filteredUsers.length > 0 ? (
        <div className="users-list">
          <div className="users-header">
            <span className="col-user">Người dùng</span>
            <span className="col-connection">Trạng thái kết nối</span>
            <span className="col-activity">Hoạt động</span>
            <span className="col-action">Thao tác</span>
          </div>
          {filteredUsers.map((user) => (
            <div key={user.userId} className="user-item">
              <div className="col-user">
                <div className="user-avatar">
                  {user.avatarUrl ? (
                    <img src={user.avatarUrl} alt={user.fullName} />
                  ) : (
                    <FaUser />
                  )}
                </div>
                <div className="user-info">
                  <h3>{user.fullName}</h3>
                  <p>{user.email}</p>
                </div>
              </div>
              
              <div className="col-connection">
                {renderConnectionStatus(user.stravaConnected, user.stravaTokenExpireDate)}
              </div>
              
              <div className="col-activity">
                <span className="activity-count">
                  {user.activityCount} hoạt động
                </span>
                {user.lastActivityDate && (
                  <span className="last-activity">
                    <FaCalendarAlt /> Hoạt động gần nhất: {formatDateFromArray(user.lastActivityDate)}
                  </span>
                )}
              </div>
              
              <div className="col-action">
                <button
                  className="sync-button"
                  onClick={() => handleSyncUser(user.userId)}
                  disabled={syncingUsers[user.userId] || !user.stravaConnected}
                  title={!user.stravaConnected ? "Người dùng chưa kết nối Strava" : "Đồng bộ hoạt động"}
                >
                  {syncingUsers[user.userId] ? (
                    <FaSpinner className="spinning" />
                  ) : (
                    <FaSync />
                  )}
                  Đồng bộ
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <FaExclamationTriangle />
          <p>
            {searchQuery 
              ? `Không tìm thấy người dùng nào phù hợp với từ khóa "${searchQuery}"` 
              : `Không có người dùng nào${showOnlyDisconnected ? " chưa kết nối Strava" : ""}. ${!showOnlyDisconnected ? "Hãy thêm người dùng trước khi đồng bộ." : ""}`
            }
          </p>
        </div>
      )}

      {/* Hiển thị dialog kết quả đồng bộ */}
      <SyncResultDialog />
    </div>
  );
};

export default Sync; 